import React, { useState } from 'react';
import { FiClock, FiMail, FiPhone, FiAlertTriangle, FiCheckCircle, FiCopy } from 'react-icons/fi';
import { FaCheck } from 'react-icons/fa';

const SubmissionConfirmation = ({ uniqueFormId, timestamp }) => {
  const [copied, setCopied] = useState(false);
  const submissionDate = new Date(timestamp);
  const formattedDate = submissionDate.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  const formattedTime = submissionDate.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });

  const copyToClipboard = () => {
    navigator.clipboard.writeText(uniqueFormId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Main Content */}
      <main className="max-w-8xl mx-auto px-4 py-8 sm:px-6 lg:px-8 mt-16">
        <div className="bg-white shadow-lg rounded-lg overflow-hidden border border-gray-200">
          {/* Status Header */}
          <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-5 sm:px-8 sm:py-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between">
              <div className="flex items-center mb-4 sm:mb-0">
                <div className="bg-white bg-opacity-20 p-2 rounded-full mr-4">
                  <FiCheckCircle className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white">Application Submission Confirmed</h2>
                  <p className="text-green-100 text-sm sm:text-base">Your pension application is being processed</p>
                </div>
              </div>
              <div className="bg-white px-4 py-2 rounded-md shadow-sm">
                <div className="flex items-center">
                  <span className="text-green-700 font-medium">Form ID: {uniqueFormId}</span>
                  <button 
                    onClick={copyToClipboard}
                    className="ml-2 p-1 text-green-600 hover:text-green-800 rounded hover:bg-green-50 transition-colors"
                    aria-label="Copy reference number"
                  >
                    {copied ? (
                      <FaCheck className="h-4 w-4 text-green-600" />
                    ) : (
                      <FiCopy className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {copied && (
                  <div className="text-xs text-green-600 mt-1 animate-fade-in">Copied to clipboard!</div>
                )}
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="p-6 sm:p-8">
            {/* Alert Notice */}
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8 rounded-r">
              <div className="flex">
                <FiAlertTriangle className="h-5 w-5 text-yellow-500 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Payment Verification Pending</h3>
                  <p className="text-gray-700 mt-1">
                    Your application has been submitted successfully but payment verification is pending. 
                    This process typically takes 2-3 working days.
                  </p>
                </div>
              </div>
            </div>

            {/* Two Column Layout */}
            <div className="grid md:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="md:col-span-2">
                <section className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-3 mb-4">Application Details</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Submission Date</p>
                      <p className="text-gray-800 font-medium">{formattedDate}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Submission Time</p>
                      <p className="text-gray-800 font-medium">{formattedTime}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Application Status</p>
                      <div className="flex items-center">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Payment Verification Pending
                        </span>
                      </div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Form ID</p>
                      <div className="flex items-center">
                        <span className="font-mono text-gray-800 mr-2">{uniqueFormId}</span>
                        <button 
                          onClick={copyToClipboard}
                          className="text-gray-400 hover:text-gray-600"
                          aria-label="Copy reference number"
                        >
                          {copied ? (
                            <FaCheck className="h-4 w-4 text-green-600" />
                          ) : (
                            <FiCopy className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-3 mb-6">Application Process</h3>
                  <div className="space-y-6">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 relative">
                        <div className="flex items-center justify-center h-10 w-10 rounded-full bg-green-100 text-green-600">
                          <span className="font-medium">1</span>
                        </div>
                        <div className="absolute top-10 left-1/2 w-0.5 h-6 -ml-px bg-green-200"></div>
                      </div>
                      <div className="ml-4">
                        <h4 className="text-base font-medium text-gray-800">Submission Received</h4>
                        <p className="text-gray-600 text-sm mt-1">
                          Your application has been successfully submitted on {formattedDate} at {formattedTime}.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="flex-shrink-0 relative">
                        <div className="flex items-center justify-center h-10 w-10 rounded-full bg-yellow-100 text-yellow-600">
                          <span className="font-medium">2</span>
                        </div>
                        <div className="absolute top-10 left-1/2 w-0.5 h-6 -ml-px bg-yellow-200"></div>
                      </div>
                      <div className="ml-4">
                        <h4 className="text-base font-medium text-gray-800">Payment Verification</h4>
                        <p className="text-gray-600 text-sm mt-1">
                          Our team will verify your payment details within 2-3 working days.
                        </p>
                        <div className="mt-2 flex items-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Current Status: Pending
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <div className="flex items-center justify-center h-10 w-10 rounded-full bg-gray-100 text-gray-400">
                          <span className="font-medium">3</span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <h4 className="text-base font-medium text-gray-500">Final Result</h4>
                        <p className="text-gray-400 text-sm mt-1">
                          You will receive final result link notification via email and SMS.
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-3 mb-4">Important Instructions</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <div className="flex-shrink-0 mt-1.5">
                        <div className="h-2 w-2 rounded-full bg-green-600"></div>
                      </div>
                      <p className="ml-3 text-gray-700">Please keep your Form ID for all future correspondence</p>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 mt-1.5">
                        <div className="h-2 w-2 rounded-full bg-green-600"></div>
                      </div>
                      <p className="ml-3 text-gray-700">Do not submit multiple applications for the same purpose</p>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 mt-1.5">
                        <div className="h-2 w-2 rounded-full bg-green-600"></div>
                      </div>
                      <p className="ml-3 text-gray-700">Check your email (including spam folder) regularly for updates</p>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 mt-1.5">
                        <div className="h-2 w-2 rounded-full bg-green-600"></div>
                      </div>
                      <p className="ml-3 text-gray-700">Contact helpdesk if you don't receive payment verification within 3 working days</p>
                    </li>
                  </ul>
                </section>
              </div>

              {/* Sidebar */}
              <div className="md:col-span-1">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-3 mb-4">Help & Support</h3>
                  
                  <div className="space-y-5">
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Contact Details</h4>
                      <div className="space-y-4">
                        <div className="flex items-start">
                          <div className="flex-shrink-0 mt-1">
                            <FiMail className="h-5 w-5 text-green-600" />
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-700">Email Support</p>
                            <a 
                              href="mailto:admin@himanshusalarycorner.in" 
                              className="text-sm text-green-600 hover:underline break-all"
                            >
                              admin@himanshusalarycorner.in
                            </a>
                          </div>
                        </div>
                        <div className="flex items-start">
                          <div className="flex-shrink-0 mt-1">
                            <FiPhone className="h-5 w-5 text-green-600" />
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-700">Helpline Number</p>
                            <a 
                              href="tel:+919529402894" 
                              className="text-sm text-green-600 hover:underline"
                            >
                              +91 9529402894
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Working Hours</h4>
                      <div className="flex items-start">
                        <div className="flex-shrink-0 mt-1">
                          <FiClock className="h-5 w-5 text-green-600" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-gray-700">
                            <span className="font-medium">Monday to Friday:</span> 9:30 AM - 6:00 PM<br />
                            <span className="font-medium">Saturday:</span> 9:30 AM - 2:00 PM<br />
                            <span className="font-medium">Closed:</span> Sundays and Public Holidays
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 border-l-4 border-green-600 p-4 rounded-r">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Need Immediate Help?</h4>
                      <p className="text-xs text-gray-600">
                        For urgent queries during working hours, please call our helpline number. 
                        Average response time is under 5 minutes.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SubmissionConfirmation;